import React, { useState } from 'react'
import { css, keyframes } from 'emotion'
import { Spacer, Text, View } from 'oio-react'
import pluralize from 'pluralize'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { CheckmarkIcon, LikeHeartIcon, MoreIcon } from 'assets/icons'
import FileListItemRowSmall from 'src/apps/Files/components/FileListItemRowSmall'
import { downloadFileAsset } from 'src/components/utils'
import RichText from 'src/components/utils/RichText'
import { useUpdateMessageLiked } from 'src/graphql/hooks'
import MessageInput from '~/Messages/components/Input'
import ReplyListItem from '~/Messages/components/ReplyListItem'

const animateOut = keyframes`
   0% {
      opacity: 1;
   }

   80% {
      opacity: 1;
   }

   100% {
      opacity: 0;
   }
`

// =======================================================
// Component
// =======================================================

const MessageListItem = ({
   id,
   allowMarkClosingMessage,
   allowMessageCreate,
   anon,
   authorAvatarUrl,
   authorId,
   authorName,
   body,
   borderStyle,
   edited,
   currentUserCanModerate,
   dateAdded,
   isClosingMessage,
   isLikedByMe,
   isMine,
   numLikes,
   onCloseMessageButtonClick,
   onOptionsButtonClick,
   onReplyOptionsButtonClick,
   replies,
   showNewMessageAnchor
}) => {
   const { updateMessageLiked } = useUpdateMessageLiked()
   const [replyInputIsVisible, setReplyInputIsVisible] = useState(false)
   const avatarUrl = authorAvatarUrl || '/assets/images/avatarPlaceholder.svg'

   // TODO: Improve this later
   // We check that the second block (element[1]) is a files block
   // (this is to make sure legacy GNS3 discussions dont blow up)
   // See PR #563, #551
   const hasFileAttachments = body.elements?.[1]?.__typename === 'FileListElement' &&
      body.elements?.[1]?.fileInstances.length > 0

   const handleMessageLikeToggled = async (liked) => {
      await updateMessageLiked({ id, liked }, {
         optimisticResponse: {
            updateMessageLiked: {
               __typename: 'HumanMessage',
               id,
               isLikedByMe: liked,
               numLikes: liked
                  ? numLikes + 1
                  : numLikes - 1
            }
         }
      })
   }

   return (
      <View
         float="left"
         width="100%"
         border={isClosingMessage && '2px solid rgb(69, 194, 145)'}
         borderRadius={isClosingMessage && '2px'}
         borderTop={!isClosingMessage && borderStyle}>
         {isClosingMessage && (
            <View
               display="flex"
               alignItems="center"
               backgroundColor="rgb(69, 194, 145)"
               padding="10px 15px 15px 15px">
               <CheckmarkIcon width="12px" height="12px" color="#fff" />
               <Spacer size="2" orientation="vertical" />
               <Text size="1.5" weight="medium" color="#fff">
                  This answers the question
               </Text>
            </View>
         )}
         <View
            display="flex"
            padding={isClosingMessage
               ? '15px'
               : '15px 0px[a-b] 15px 10px[c-f]'
            }>
            <View
               flex="0 0 auto"
               float="left"
               width="30px"
               height="30px"
               borderRadius="50%"
               backgroundColor="#ddd"
               backgroundImage={`url(${avatarUrl})`}
               backgroundSize={authorAvatarUrl ? 'cover' : '21px 21px'}
               backgroundPosition="bottom center"
               backgroundRepeat="no-repeat"
               marginRight="20px"
            />
            <View flex="1 1 auto" maxWidth="calc(100% - 50px)">
               <View
                  display="flex"
                  justifyContent="space-between">
                  <View
                     display="block[a-b] flex[c-f]"
                     alignItems="center">
                     <View paddingRight="6px">
                        {!anon && (
                           <Link to={`/profile/${authorId}`}>
                              <Text size="1.5[a-d] 2[e-f]" color="#222" weight="medium">
                                 {anon ? 'Anonymous' : authorName}
                              </Text>
                           </Link>
                        )}
                        {anon && (
                           <Text size="1.5[a-d] 2[e-f]" color="#222" weight="medium">
                              Anonymous
                           </Text>
                        )}
                     </View>
                     <Text size="1" color="#888" uppercase>
                        {dateAdded}
                        {edited && (
                           <View display="inline-block" marginLeft="6px">
                              <Text size="1" color="#aaa">[edited]</Text>
                           </View>
                        )}
                     </Text>
                  </View>
                  {/* TODO: Other people with message edit or remove privileges
                     should be able to also edit or remove the message */}
                  {(isMine || currentUserCanModerate) && (
                     <View
                        position="relative"
                        top="-3px"
                        height="18px"
                        onClick={onOptionsButtonClick}>
                        <MoreIcon
                           width="24px"
                           height="24px"
                           color="#aaa"
                        />
                     </View>
                  )}
               </View>
               <View display="block" float="left" width="100%">
                  <Spacer size="1" />
                  <Text size="2[a-d] 2.5[e-f]" lineHeight="140%" color="#555">
                     <RichText html={body?.elements?.[0]?.body} />
                  </Text>
                  {hasFileAttachments && (
                     <View
                        float="left"
                        width="100%"
                        margin="15px 0px"
                        borderTop="1px solid #eee"
                        borderLeft="1px solid #eee"
                        borderRight="1px solid #eee">
                        {body.elements[1].fileInstances.map(instance => (
                           <FileListItemRowSmall
                              key={instance.file.id}
                              id={instance.file.id}
                              infected={instance.file.infected}
                              mimetypeMismatch={instance.file.mimetypeMismatch}
                              mimetypeVerified={instance.file.mimetypeVerified}
                              name={instance.file.name}
                              onDownloadButtonClick={() => {
                                 downloadFileAsset(instance.file.id)
                              }}
                              paddingHorizontal="5px"
                              showDownloadButton
                              status={instance.file.status}
                              thumbnailUrl={instance.file.thumbnailUrlW48}
                           />
                        ))}
                     </View>
                  )}
                  {numLikes > 0 && (
                     <View float="left" width="100%" marginTop="6px">
                        <Text
                           size="1"
                           lineHeight="150%"
                           color="#aaa">
                           {`${pluralize('people', numLikes, true)} like${numLikes === 1 ? 's' : ''} this`}
                        </Text>
                     </View>
                  )}
               </View>
               <View float="left" width="100%" display="block">
                  <Spacer size="1" />
                  <View display="flex" alignItems="center">
                     <View
                        onClick={() => handleMessageLikeToggled(!isLikedByMe)}
                        display="inline-flex"
                        alignItems="center"
                        style={{ cursor: 'pointer' }}>
                        {isLikedByMe && (
                           <View display="inline-block" paddingRight="6px">
                              <LikeHeartIcon width="12px" height="12px" color="#ff5b5b" />
                           </View>
                        )}
                        <Text
                           size="0.8"
                           weight="bold"
                           transform="uppercase"
                           color={isLikedByMe ? '#ff5b5b' : '#888'}>
                           {isLikedByMe ? 'Liked' : 'Like'}
                        </Text>
                     </View>
                     {allowMessageCreate && !replyInputIsVisible && (
                        <View
                           onClick={() => setReplyInputIsVisible(true)}
                           display="inline-flex"
                           alignItems="center"
                           style={{ cursor: 'pointer' }}>
                           <Spacer size="1.5" orientation="vertical" />
                           <Text size="1" weight="bold" color="#888">
                              &#183;
                           </Text>
                           <Spacer size="1.5" orientation="vertical" />
                           <Text size="0.8" weight="bold" color="#888" transform="uppercase">
                              Reply
                           </Text>
                        </View>
                     )}
                     {(isMine || currentUserCanModerate) && allowMarkClosingMessage && (
                        <View
                           onClick={onCloseMessageButtonClick}
                           display="inline-flex"
                           alignItems="center"
                           style={{ cursor: 'pointer' }}>
                           <Spacer size="1.5" orientation="vertical" />
                           <Text size="1" weight="bold" color="#888">
                              &#183;
                           </Text>
                           <Spacer size="1.5" orientation="vertical" />
                           <Text size="0.8" weight="bold" color="#888" transform="uppercase">
                              Mark as Answer
                           </Text>
                        </View>
                     )}
                  </View>
                  {replies.map(reply => (
                     <ReplyListItem
                        key={reply.id}
                        id={reply.id}
                        anon={reply.anon}
                        authorAvatarUrl={reply?.author?.avatarMedia?.file.thumbnailUrlW48}
                        authorId={reply?.author?.id}
                        authorName={reply?.author?.fullName}
                        body={reply.body?.elements?.[0]?.body}
                        currentUserCanModerate={currentUserCanModerate}
                        edited={reply.edited}
                        dateAdded={reply.dateAdded}
                        isLikedByMe={reply.isLikedByMe}
                        isMine={reply.isMine}
                        messageId={id}
                        numLikes={reply.numLikes}
                        onOptionsButtonClick={e => onReplyOptionsButtonClick(e, reply.id)}
                     />
                  ))}
                  {replyInputIsVisible && (
                     <View float="left" width="100%" paddingTop="10px">
                        <MessageInput
                           inputToolbarConfig="textBasic"
                           messageId={id}
                           onCancel={() => setReplyInputIsVisible(false)}
                           onSubmit={() => setReplyInputIsVisible(false)}
                        />
                     </View>
                  )}
               </View>
            </View>
         </View>
         {showNewMessageAnchor && (
            <View
               className={css`
                  opacity: 0;
                  animation: ${animateOut} 10s ease-out;
               `}
               position="relative"
               top="1px"
               borderTop="1px solid #3893f2">
               <View
                  display="flex"
                  alignItems="center"
                  height="16px"
                  position="absolute"
                  top="-8px"
                  padding="0px 10px"
                  borderRadius="2px"
                  backgroundColor="#3893f2">
                  <Text size="0.8" weight="medium" transform="uppercase" color="#fff">
                     New
                  </Text>
               </View>
            </View>
         )}
      </View>
   )
}

MessageListItem.propTypes = {
   id: PropTypes.string.isRequired,
   allowMarkClosingMessage: PropTypes.bool,
   allowMessageCreate: PropTypes.bool.isRequired,
   anon: PropTypes.bool.isRequired,
   authorAvatarUrl: PropTypes.string,
   authorId: PropTypes.string,
   authorName: PropTypes.string,
   body: PropTypes.object.isRequired,
   borderStyle: PropTypes.string,
   currentUserCanModerate: PropTypes.bool.isRequired,
   edited: PropTypes.bool.isRequired,
   dateAdded: PropTypes.string.isRequired,
   isClosingMessage: PropTypes.bool,
   isLikedByMe: PropTypes.bool.isRequired,
   isMine: PropTypes.bool.isRequired,
   numLikes: PropTypes.number.isRequired,
   onCloseMessageButtonClick: PropTypes.func,
   onOptionsButtonClick: PropTypes.func.isRequired,
   onReplyOptionsButtonClick: PropTypes.func.isRequired,
   replies: PropTypes.array.isRequired,
   showNewMessageAnchor: PropTypes.bool
}

MessageListItem.defaultProps = {
   allowMarkClosingMessage: false,
   authorAvatarUrl: undefined,
   authorId: undefined,
   authorName: undefined,
   borderStyle: '1px solid #e5e5e5',
   isClosingMessage: false,
   onCloseMessageButtonClick: undefined,
   showNewMessageAnchor: false
}

export default MessageListItem
